import React, { useState, useMemo } from "react";
import { OrdersTable } from "@/components/financeiro/OrdersTable";
import { FinanceFilterBar, type DateRangeValue } from "@/components/financeiro/FinanceFilterBar";
import { useAppSelector } from "@/store/hooks";
import { Info } from "lucide-react";
import { Alert, AlertDescription, Text } from "@musa.tech/design-system";

const Financeiro = () => {
  const [dueDateRange, setDueDateRange] = useState<DateRangeValue | null>(null);
  const [billingPeriods, setBillingPeriods] = useState<string[]>([]);
  const [establishmentIds, setEstablishmentIds] = useState<number[]>([]);
  const [generatorIds, setGeneratorIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [releaseStatuses, setReleaseStatuses] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);

  const rawGenerators = useAppSelector((s) => s.establishmentAccess.generators);

  // Mapeia external_ids selecionados para IDs internos inteiros (padrão do filtro generator_id da
  // API). Vai como `generator_id`, e a API expande a subárvore: a página não traduz mais o gerador
  // em lista de filiais — com centenas de lojas a URL estourava (414).
  const generatorIdForApi = useMemo(() => {
    if (!generatorIds.length) return undefined;
    return generatorIds
      .map((externalId) => {
        const found = rawGenerators.find(
          (g) => g.external_id === externalId || g.id === externalId,
        );
        return found ? parseInt(found.id, 10) : null;
      })
      .filter((id): id is number => id !== null);
  }, [generatorIds, rawGenerators]);

  const handleGeneratorIdsChange = (ids: string[]) => {
    setGeneratorIds(ids);
    setEstablishmentIds([]);
  };

  const handleClearFilters = () => {
    setDueDateRange(null);
    setBillingPeriods([]);
    setEstablishmentIds([]);
    setGeneratorIds([]);
    setStatusFilter('all');
    setReleaseStatuses([]);
    setTypes([]);
  };

  // `bg-neutral-100` e não `bg-background`: este resolvia para branco puro e destoava da home,
  // que é o fundo de referência das telas do portal (retorno de QA, 24/08/2026).
  return (
    <div className="min-h-screen bg-neutral-100 p-4 md:p-6 w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 min-w-0">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <Text as="h1" variant="heading" size="large" className="text-2xl md:text-3xl text-neutral-900">
            Financeiro
          </Text>
          <Text as="p" variant="body" className="text-neutral-500 mt-2 block text-sm md:text-base">
            Acompanhe suas medições e faturas
          </Text>
        </div>

        {/* Extrato vazio é extrato vazio: aviso, filtros e tabela continuam em tela, e a
            ausência é dita dentro da própria listagem (ver o estado vazio de `OrdersTable`).
            A página antes se substituía por um "Página em construção" quando o gerador não
            tinha nenhuma fatura — descrevia o produto como inacabado em vez de descrever os
            dados dele, e sumia com os filtros de quem tinha medição em análise e nenhuma
            fatura emitida. A checagem que alimentava esse desvio era uma segunda consulta a
            `useInvoices` só para contar, que saiu junto. */}

        {/* Aviso de prazo de pagamento */}
        <Alert className="border-info-500 bg-primary-50 text-neutral-700">
          <Info className="h-4 w-4 text-info-500" />
          <AlertDescription>
            A identificação e atualização do pagamento pode levar até 3 dias úteis após a compensação bancária.
          </AlertDescription>
        </Alert>

        {/* Filtros */}
        <FinanceFilterBar
          dueDateRange={dueDateRange}
          billingPeriods={billingPeriods}
          establishmentIds={establishmentIds}
          generatorIds={generatorIds}
          statusFilter={statusFilter}
          releaseStatuses={releaseStatuses}
          types={types}
          onDueDateRangeChange={setDueDateRange}
          onBillingPeriodsChange={setBillingPeriods}
          onEstablishmentIdsChange={setEstablishmentIds}
          onGeneratorIdsChange={handleGeneratorIdsChange}
          onStatusFilterChange={setStatusFilter}
          onReleaseStatusesChange={setReleaseStatuses}
          onTypesChange={setTypes}
          onClearFilters={handleClearFilters}
        />

        {/* Extrato financeiro */}
        <OrdersTable
          dueDateRange={dueDateRange}
          billingPeriods={billingPeriods}
          generatorId={generatorIdForApi}
          establishmentIds={establishmentIds.length ? establishmentIds : undefined}
          statusFilter={statusFilter}
          releaseStatuses={releaseStatuses}
          types={types}
          onOpenDetail={(sfmId) => window.open(`/financeiro/${sfmId}`, '_blank')}
        />
      </div>
    </div>
  );
};

export default Financeiro;
